/**
 * Settings -> Integrations: test mode.
 *
 * The one screen that decides whether a half-finished feature can write to a
 * real client. Its whole job is to be unambiguous about the current state, which
 * is why the head's status pill reads the switch rather than "configured": an
 * operator glancing at this card needs to know whether the net is up, not
 * whether somebody once filled the fields in.
 *
 * The warning about an enabled guard with no test inbox is shown rather than
 * prevented on save, because the server already fails closed on it (nothing is
 * sent) and a form that refuses to save a half-finished state loses the work.
 */
import { useState, useEffect } from "react";
import { FlaskConical, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getMailGuard, saveMailGuard, type MailGuard } from "../../../../lib/mail";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";

/** One entry per line is what an operator types; the API wants an array. */
function toLines(list: string[]): string {
  return list.join("\n");
}
function fromLines(text: string): string[] {
  return text.split("\n").map((l) => l.trim()).filter(Boolean);
}

export function TestModeCard() {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [redirectTo, setRedirectTo] = useState("");
  const [allowlist, setAllowlist] = useState("");

  function hydrate(data: MailGuard) {
    setEnabled(data.enabled);
    setRedirectTo(data.redirectTo);
    setAllowlist(toLines(data.allowlist));
  }

  useEffect(() => {
    getMailGuard().then(hydrate).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      hydrate(await saveMailGuard({ enabled, redirectTo, allowlist: fromLines(allowlist) }));
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  const noInbox = enabled && !redirectTo.trim();

  return (
    <IntegrationCard
      icon={FlaskConical}
      title={t("settings.testMode.title")}
      desc={t("settings.testMode.desc")}
      status={
        <ConnStatus
          ok={enabled}
          okLabel={t("settings.testMode.statusOn")}
          offLabel={t("settings.testMode.statusOff")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-6">
        <Note tone={enabled ? "warn" : "mut"}>
          {enabled ? t("settings.testMode.onNote") : t("settings.testMode.offNote")}
        </Note>

        <FieldGroup title={t("settings.testMode.switchGroup")}>
          <ToggleRow
            label={t("settings.testMode.enabled")}
            hint={t("settings.testMode.enabledHint")}
            checked={enabled}
            onChange={setEnabled}
          />
        </FieldGroup>

        <FieldGroup title={t("settings.testMode.routing")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.testMode.redirectTo")} hint={t("settings.testMode.redirectToHint")}>
              <Pill
                value={redirectTo}
                onChange={setRedirectTo}
                type="email"
                placeholder={t("common.example.yourEmail")}
                autoComplete="off"
              />
            </Field>
            <Field label={t("settings.testMode.allowlist")} hint={t("settings.testMode.allowlistHint")}>
              {/* `crm-area`, not a textarea inside `crm-inp`: that pill is a fixed
                  flex row whose child rules only reach input and select. */}
              <textarea
                className="crm-area min-h-[92px] font-mono text-[12.25px]"
                value={allowlist}
                rows={3}
                onChange={(e) => setAllowlist(e.target.value)}
                placeholder={t("common.example.allowlist")}
              />
            </Field>
          </div>
        </FieldGroup>

        {noInbox ? <Note tone="bad">{t("settings.testMode.noInboxWarning")}</Note> : null}

        <FieldGroup title={t("settings.testMode.scope")}>
          <Note tone="info">{t("settings.testMode.scopeNote")}</Note>
        </FieldGroup>

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.mail.saving") : t("settings.mail.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.mail.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.mail.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}
